/**
 * Subscribe Button.
 * @format
 */

import React, { useState, useEffect } from 'react';
import { useCookies } from 'react-cookie';
import ModalWrapper from '@components/modals/modal-wrapper';

/**
 * Japan Products Disclaimer.
 *
 * @returns JSX.Element | null
 */
const JaProductsDisclaimer = (): JSX.Element | null => {
  const [showModal, setShowModal] = useState<boolean>(true);
  const [count, setCount] = useState<number>(0);
  const [countActive, setCountActive] = useState<boolean>(false);
  const [cookies, setCookie] = useCookies(['ja-disclaimer']);
  const expireDate = new Date();
  expireDate.setDate(expireDate.getDate() + 30);

  /**
   * Accept disclaimer.
   */
  const acceptDisclaimer = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null
  ) => {
    if (event) {
      event.preventDefault();
    }
    setCountActive(false);
    setShowModal(false);
    setCookie('ja-disclaimer', 'true', { path: '/', expires: expireDate });
  };

  /**
   * Decline disclaimer.
   */
  const declineDisclaimer = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null
  ) => {
    event?.preventDefault();
    setCount(10);
    setCountActive(true);
  };

  /**
   * Start countdown.
   */
  useEffect(() => {
    let interval: NodeJS.Timer;
    if (countActive && count >= 0) {
      interval = setInterval(() => {
        setCount((count: number) => count - 1);
        if (count === 0) {
          setShowModal(false);
          setCountActive(false);
          window.location = '/ja/';
          clearInterval(interval);
          setCount(-1);
        }
      }, 1000);
    }

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, countActive]);

  /**
   *  Reset initial state
   */
  const resetState = () => {
    if (!cookies['ja-disclaimer']) {
      setShowModal(true);
      setCountActive(false);
      setCount(0);
    }
  };

  /**
   * Add event listener to call resetState()
   * when navigating forward/back in browser
   */
  useEffect(() => {
    window.addEventListener('pageshow', resetState);
    return () => window.removeEventListener('pageshow', resetState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Listens for modal to close.
   */
  useEffect(() => {
    if (!showModal && !cookies['ja-disclaimer']) {
      window.location = '/ja/';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  /**
   * Render.
   */
  if (!cookies['ja-disclaimer']) {
    return (
      <ModalWrapper showModal={showModal} setShowModal={setShowModal}>
        <>
          {/* Step 1 */}
          {count === 0 && (
            <div className={`step-1`}>
              <h2 className="text-align-center font-size-lg">ご注意</h2>
              <p>
                このページから先は、Varian
                の製品に関する情報を、医療関係者の方に提供することを目的として作成されています。
                一般の方への情報提供を目的としたものではありませんのでご了承ください。
              </p>

              <h3 className="text-align-center">あなたは医療関係者ですか？</h3>

              <div className={`medium-vertical-margin text-center`}>
                {/* Accept */}
                <button
                  className="btn-primary"
                  type="button"
                  onClick={(e) => acceptDisclaimer(e)}>
                  はい
                </button>

                <span className={'small-horizontal-margin'} />

                {/* Decline */}
                <button
                  className="btn-secondary"
                  onClick={(e) => declineDisclaimer(e)}
                  type="button">
                  いいえ
                </button>
              </div>

              <p className="text-align-center footnote">
                ※「はい」を記憶させるには、お使いのプラウザの
                Cookie設定を有効にしてください。
              </p>
            </div>
          )}

          {/* Step 2 */}
          {count > 0 && (
            <div className={`step-2`}>
              <h2 className="text-align-center">確認</h2>
              <p>
                「いいえ」を選択した方に対してこのメッセージを表示しています。医療関係者にもかかわらず、誤って「いいえ」を選択してしまった場合、下のボタンをクリックして医療関係者向けページに進んでください。あと{' '}
                <strong>{count}</strong> 秒 でホームに移動します。
              </p>
              <div className={`medium-vertical-margin text-center`}>
                <button
                  className="btn-primary"
                  type="button"
                  onClick={acceptDisclaimer}>
                  医療関係者向けページに進む
                </button>
              </div>
            </div>
          )}
        </>
      </ModalWrapper>
    );
  }

  return null;
};
export default JaProductsDisclaimer;
